import React from 'react';

/* ═══════════════════════════════════════════════════════════════
   IMPERIAL PROGRESS BAR — shared by single + batch uploads
   The category's beast icon oscillates across the bar while the
   gold track fills with the actual upload percentage.
   ═══════════════════════════════════════════════════════════════ */

const GOLD = '#D4AF37';
const GOLD_DARK = '#B8926A';
const ICON_SIZE = 44;
const PAD = 10;

export const ImperialPulseCSS = () => (
  <style>{`
    .imperial-glide {
      animation: imperialOscillate 6s ease-in-out infinite;
    }
    @keyframes imperialOscillate {
      0%   { left: var(--track-start); }
      50%  { left: var(--track-end); }
      100% { left: var(--track-start); }
    }
    .imperial-breathe {
      animation: imperialBreathe 3s ease-in-out infinite;
    }
    @keyframes imperialBreathe {
      0%, 100% {
        box-shadow: 0 0 8px rgba(212,175,55,0.25);
        border-color: rgba(212,175,55,0.3);
        filter: brightness(0.7);
      }
      50% {
        box-shadow: 0 0 28px rgba(212,175,55,0.8), 0 0 56px rgba(212,175,55,0.3);
        border-color: rgba(212,175,55,0.9);
        filter: brightness(1.15);
      }
    }
    .imperial-track-fill {
      animation: trackBreath 3s ease-in-out infinite;
    }
    @keyframes trackBreath {
      0%, 100% { box-shadow: 0 0 3px rgba(212,175,55,0.2); }
      50%      { box-shadow: 0 0 10px rgba(212,175,55,0.5); }
    }
    .imperial-aura {
      animation: auraBreath 3s ease-in-out infinite;
    }
    @keyframes auraBreath {
      0%, 100% { opacity: 0.15; transform: scale(0.9); }
      50%      { opacity: 0.45; transform: scale(1.2); }
    }
  `}</style>
);

export const ImperialProgressBar = ({ icon, uploadPercent = 0, caption = '' }) => (
  <div className="space-y-2 pt-2" data-testid="upload-progress-animal">
    <div style={{
      position: 'relative', background: '#000000',
      height: '80px', maxWidth: '100%', overflow: 'hidden',
      borderRadius: '16px',
      border: '1px solid rgba(212,175,55,0.08)',
      '--track-start': `${PAD}px`,
      '--track-end': `calc(100% - ${PAD + ICON_SIZE}px)`,
    }}>
      <div style={{
        position: 'absolute', inset: 0,
        background: 'linear-gradient(180deg, rgba(212,175,55,0.02) 0%, transparent 60%)',
      }} />
      <div style={{
        position: 'absolute', bottom: 10, left: PAD, right: PAD,
        height: 2,
        background: 'linear-gradient(90deg, rgba(212,175,55,0.06), rgba(212,175,55,0.15), rgba(212,175,55,0.06))',
        borderRadius: 1,
      }} />
      <div className="imperial-track-fill" style={{
        position: 'absolute', bottom: 10, left: PAD,
        height: 2,
        background: `linear-gradient(90deg, ${GOLD}, ${GOLD_DARK})`,
        width: `calc((100% - ${PAD * 2}px) * ${(uploadPercent || 0) / 100})`,
        borderRadius: 1,
        transition: 'width 0.3s ease-out',
      }} />
      <div
        className="imperial-glide"
        style={{
          position: 'absolute',
          bottom: 12,
          width: ICON_SIZE,
          height: ICON_SIZE,
        }}
      >
        <div className="imperial-aura" style={{
          position: 'absolute',
          inset: -8,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(212,175,55,0.3) 0%, transparent 70%)',
        }} />
        <div
          className="imperial-breathe"
          style={{
            width: ICON_SIZE,
            height: ICON_SIZE,
            borderRadius: '50%',
            overflow: 'hidden',
            border: '1.5px solid rgba(212,175,55,0.5)',
            position: 'relative',
          }}
        >
          {icon && (
            <img
              src={icon.src}
              alt="Beast"
              style={{
                width: '100%', height: '100%',
                objectFit: 'cover', objectPosition: icon.pos,
              }}
              data-testid="beast-icon-progress"
            />
          )}
        </div>
      </div>
    </div>
    {caption && (
      <p className="font-['Outfit'] text-[10px] text-white/20 text-center tracking-wider">
        {caption}
      </p>
    )}
  </div>
);
